// IndexedDB database service using Dexie
import { INDEXEDDB_CONFIG } from '@shared/constants';
import { AudioRecording, Photo, SyncQueue, SyncStatus, Task } from '@shared/types';
import Dexie, { Table } from 'dexie';

// Database interface
export interface FarmTasksDB extends Dexie {
  tasks: Table<Task>;
  photos: Table<Photo>;
  audioRecordings: Table<AudioRecording>;
  syncQueue: Table<SyncQueue>;
  userData: Table<any>;
  settings: Table<any>;
}

// Database instance
let database: FarmTasksDB;

/**
 * Initialize the database
 */
export async function initializeDatabase(): Promise<void> {
  try {
    database = new Dexie(INDEXEDDB_CONFIG.DATABASE_NAME) as FarmTasksDB;

    // Define schemas
    database.version(INDEXEDDB_CONFIG.VERSION).stores({
      tasks: 'id, userId, status, category, createdAt, syncStatus, localId',
      photos: 'id, taskId, syncStatus, capturedAt',
      audioRecordings: 'id, taskId, syncStatus, recordedAt',
      syncQueue: '++id, type, resourceType, resourceId, createdAt, retryCount',
      userData: 'id, type',
      settings: 'key',
    });

    // Add hooks for automatic timestamps
    database.tasks.hook('creating', (_primKey, obj, _trans) => {
      obj.createdAt = new Date().toISOString();
      obj.updatedAt = new Date().toISOString();
    });

    database.tasks.hook('updating', (modifications) => {
      (modifications as any).updatedAt = new Date().toISOString();
    });

    await database.open();
    console.log('Database opened successfully');
  } catch (error) {
    console.error('Failed to initialize database:', error);
    throw error;
  }
}

/**
 * Get database instance
 */
export function getDatabase(): FarmTasksDB {
  if (!database) {
    throw new Error('Database not initialized. Call initializeDatabase() first.');
  }
  return database;
}

/**
 * Task operations
 */
export const TasksDB = {
  async create(task: Omit<Task, 'id'>): Promise<string> {
    const id = await database.tasks.add(task as Task);
    return id as string;
  },

  async getAll(): Promise<Task[]> {
    return await database.tasks.orderBy('createdAt').reverse().toArray();
  },

  async getById(id: string): Promise<Task | undefined> {
    return await database.tasks.get(id);
  },

  async getByStatus(status: string): Promise<Task[]> {
    return await database.tasks.where('status').equals(status).toArray();
  },

  async getUnsynced(): Promise<Task[]> {
    return await database.tasks.where('syncStatus').anyOf(['pending', 'error']).toArray();
  },

  async update(id: string, changes: Partial<Task>): Promise<void> {
    await database.tasks.update(id, changes);
  },

  async delete(id: string): Promise<void> {
    await database.tasks.delete(id);
  },

  async clear(): Promise<void> {
    await database.tasks.clear();
  },
};

/**
 * Photo operations
 */
export const PhotosDB = {
  async create(photo: Photo): Promise<string> {
    const id = await database.photos.add(photo);
    return id.toString();
  },

  async getByTaskId(taskId: string): Promise<Photo[]> {
    return await database.photos.where('taskId').equals(taskId).toArray();
  },

  async getUnsynced(): Promise<Photo[]> {
    return await database.photos.where('syncStatus').equals('pending').toArray();
  },

  async update(id: string, changes: Partial<Photo>): Promise<number> {
    return await database.photos.update(id, changes);
  },

  async delete(id: string): Promise<void> {
    await database.photos.delete(id);
  },

  async clear(): Promise<void> {
    await database.photos.clear();
  }
};

/**
 * Audio recording operations
 */
export const AudioRecordingsDB = {
  async create(audio: AudioRecording): Promise<string> {
    const id = await database.audioRecordings.add(audio);
    return id.toString();
  },

  async getByTaskId(taskId: string): Promise<AudioRecording[]> {
    return await database.audioRecordings.where('taskId').equals(taskId).toArray();
  },

  async getUnsynced(): Promise<AudioRecording[]> {
    return await database.audioRecordings.where('syncStatus').equals('pending').toArray();
  },

  async update(id: string, changes: Partial<AudioRecording>): Promise<number> {
    return await database.audioRecordings.update(id, changes);
  },

  async delete(id: string): Promise<void> {
    await database.audioRecordings.delete(id);
  },

  async clear(): Promise<void> {
    await database.audioRecordings.clear();
  }
};

/**
 * Sync queue operations
 */
export const SyncQueueDB = {
  async add(item: Omit<SyncQueue, 'id'>): Promise<number> {
    const id = await database.syncQueue.add(item as SyncQueue);
    return Number(id);
  },

  async getAll(): Promise<SyncQueue[]> {
    return await database.syncQueue.orderBy('createdAt').toArray();
  },

  async getPending(): Promise<SyncQueue[]> {
    return await database.syncQueue.where('retryCount').below(3).toArray();
  },

  async update(id: number, changes: Partial<SyncQueue>): Promise<void> {
    await database.syncQueue.update(id, changes);
  },

  async delete(id: number): Promise<void> {
    await database.syncQueue.delete(id);
  },

  async clear(): Promise<void> {
    await database.syncQueue.clear();
  },

  async addToSyncQueue(item: Omit<SyncQueue, 'id'>): Promise<number> {
    const id = await database.syncQueue.add(item as SyncQueue);
    return Number(id);
  },

  // Clear all data (for testing/reset)
  async clearAll(): Promise<void> {
    await Promise.all([
      TasksDB.clear(),
      PhotosDB.clear(),
      AudioRecordingsDB.clear(),
      SyncQueueDB.clear()
    ]);
  },
};

/**
 * Settings operations
 */
export const SettingsDB = {
  async set(key: string, value: any): Promise<void> {
    await database.settings.put({ key, value });
  },

  async get(key: string): Promise<any> {
    const result = await database.settings.get(key);
    return result?.value;
  },

  async delete(key: string): Promise<void> {
    await database.settings.delete(key);
  },
};

/**
 * Utility functions
 */
export const DatabaseUtils = {
  async getStorageUsage(): Promise<{ used: number; quota: number; available: number }> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      const used = estimate.usage || 0;
      const quota = estimate.quota || 0;
      const available = quota - used;
      
      return { used, quota, available };
    }
    
    return { used: 0, quota: 0, available: 0 };
  },

  async clearAllData(): Promise<void> {
    await Promise.all([
      TasksDB.clear(),
      PhotosDB.clear(),
      AudioRecordingsDB.clear(),
      SyncQueueDB.clear(),
    ]);
  },

  async exportData(): Promise<{
    tasks: Task[];
    photos: Photo[];
    audioRecordings: AudioRecording[];
    timestamp: string;
  }> {
    const [tasks, photos, audioRecordings] = await Promise.all([
      TasksDB.getAll(),
      database.photos.toArray(),
      database.audioRecordings.toArray(),
    ]);

    return {
      tasks,
      photos,
      audioRecordings,
      timestamp: new Date().toISOString(),
    };
  },

  async importData(data: {
    tasks: Task[];
    photos: Photo[];
    audioRecordings: AudioRecording[];
  }): Promise<void> {
    await database.transaction('rw', [database.tasks, database.photos, database.audioRecordings], async () => {
      await Promise.all([
        database.tasks.bulkPut(data.tasks),
        database.photos.bulkPut(data.photos),
        database.audioRecordings.bulkPut(data.audioRecordings),
      ]);
    });
  },
};

/**
 * Main database service export
 */
export const db = {
  initialize: initializeDatabase,
  getDatabase,
  
  // Task operations
  saveTask: async (task: Task): Promise<string> => {
    return await TasksDB.create(task);
  },
  getTasks: TasksDB.getAll,
  getTaskById: TasksDB.getById,
  getTasksByStatus: TasksDB.getByStatus,
  getUnsyncedTasks: TasksDB.getUnsynced,
  updateTask: TasksDB.update,
  deleteTask: TasksDB.delete,
  
  // Photo operations
  savePhotos: async (taskId: string, photos: File[]): Promise<void> => {
    for (const photo of photos) {
      const photoData: Photo = {
        id: crypto.randomUUID(), // Generate ID for IndexedDB
        taskId,
        filename: photo.name,
        mimeType: photo.type,
        size: photo.size,
        capturedAt: new Date().toISOString(),
        localBlob: photo,
        syncStatus: SyncStatus.PENDING,
      };
      await PhotosDB.create(photoData);
    }
  },
  getPhotosByTaskId: PhotosDB.getByTaskId,
  getUnsyncedPhotos: PhotosDB.getUnsynced,
  updatePhoto: PhotosDB.update,
  deletePhoto: PhotosDB.delete,
  
  // Audio operations
  saveAudioRecording: async (taskId: string, audio: File): Promise<void> => {
    const audioData: AudioRecording = {
      id: crypto.randomUUID(), // Generate ID for IndexedDB
      taskId,
      filename: audio.name,
      mimeType: audio.type,
      size: audio.size,
      duration: 0, // TODO: Calculate actual duration
      recordedAt: new Date().toISOString(),
      localBlob: audio,
      syncStatus: SyncStatus.PENDING,
    };
    await AudioRecordingsDB.create(audioData);
  },
  getAudioByTaskId: AudioRecordingsDB.getByTaskId,
  getUnsyncedAudio: AudioRecordingsDB.getUnsynced,
  updateAudio: AudioRecordingsDB.update,
  deleteAudio: AudioRecordingsDB.delete,
  
  // Sync queue operations
  addToSyncQueue: SyncQueueDB.add,
  getSyncQueue: SyncQueueDB.getAll,
  getPendingSyncItems: SyncQueueDB.getPending,
  updateSyncItem: SyncQueueDB.update,
  deleteSyncItem: SyncQueueDB.delete,
  clearSyncQueue: SyncQueueDB.clear,
  
  // Settings operations
  setSetting: SettingsDB.set,
  getSetting: SettingsDB.get,
  deleteSetting: SettingsDB.delete,
  
  // Utility operations
  getStorageUsage: DatabaseUtils.getStorageUsage,
  clearAllData: DatabaseUtils.clearAllData,
  exportData: DatabaseUtils.exportData,
  importData: DatabaseUtils.importData,
}; 